import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Column } from '@material-table/core';
import { Card } from '@tremor/react';
import moment from 'moment';
import appState from '../../state/AppStateContainer';
import { ServiceDashboard } from '../../services/service-dashboard';
import DataTable from '../../components/shared/DataTable/DataTable';
import { DeadlineRow, DeadlineActivityType } from './types';
import styles from './DeadlinesTab.module.scss';

const TYPE_LABELS: Record<DeadlineActivityType, [string, string]> = {
  anbudsinlamning: ['deadlines.type.anbudsinlamning', 'Anbudsinlämning'],
  aktivitet: ['deadlines.type.aktivitet', 'Aktivitet'],
  inkop_klart: ['deadlines.type.inkopKlart', 'Inköp klart'],
  forfragan_skickas_senast: ['deadlines.type.forfraganSkickasSenast', 'Förfrågan skickas senast'],
  ramavtal_utgang: ['deadlines.type.ramavtalUtgang', 'Ramavtal utgångstid'],
};

const BADGE_COLORS: Record<string, { bg: string; text: string }> = {
  red: { bg: 'rgb(254, 226, 226)', text: 'rgb(220, 38, 38)' },
  yellow: { bg: 'rgb(254, 249, 195)', text: 'rgb(161, 98, 7)' },
  gray: { bg: 'rgb(243, 244, 246)', text: 'rgb(75, 85, 99)' },
};

const urgencyColor = (daysLeft: number): string =>
  daysLeft < 0 ? 'red' : daysLeft <= 7 ? 'yellow' : 'gray';

const DaysLeftBadge: FC<{ daysLeft: number }> = ({ daysLeft }) => {
  const { bg, text } = BADGE_COLORS[urgencyColor(daysLeft)];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: 1,
        padding: '2px 10px',
        borderRadius: '9999px',
        backgroundColor: bg,
        color: text,
        whiteSpace: 'nowrap',
      }}
    >
      {daysLeft}
    </span>
  );
};

const TypeBadge: FC<{ label: string }> = ({ label }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: 1,
      padding: '3px 10px',
      borderRadius: '6px',
      backgroundColor: 'rgb(243, 244, 246)',
      color: 'rgb(75, 85, 99)',
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </span>
);

type TypeOption = { value: DeadlineActivityType; label: string };

const DeadlinesTab: FC = () => {
  const [t] = useTranslation('dashboard');
  const history = useHistory();
  const orgUrl = appState._getCurrentOrgUrl();
  const [rows, setRows] = useState<DeadlineRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [typeFilters, setTypeFilters] = useState<Set<DeadlineActivityType>>(new Set());

  useEffect(() => {
    ServiceDashboard.getAllDeadlines()
      .then((data: DeadlineRow[]) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setIsLoading(false));
  }, []);

  const overdueCount = useMemo(() => rows.filter(r => r.daysLeft < 0).length, [rows]);
  const soonCount = useMemo(() => rows.filter(r => r.daysLeft >= 0 && r.daysLeft <= 7).length, [rows]);
  const upcomingCount = useMemo(() => rows.filter(r => r.daysLeft > 7).length, [rows]);

  const typeOptions = useMemo<TypeOption[]>(
    () => (Object.keys(TYPE_LABELS) as DeadlineActivityType[]).map(key => ({ value: key, label: t(...TYPE_LABELS[key]) })),
    [t]
  );
  const toggleTypeFilter = (type: DeadlineActivityType) => {
    setTypeFilters(prev => {
      const next = new Set(prev);
      next.has(type) ? next.delete(type) : next.add(type);
      return next;
    });
  };

  const visibleRows = (typeFilters.size > 0 ? rows.filter(r => typeFilters.has(r.type)) : rows)
    .slice()
    .sort((a, b) => a.daysLeft - b.daysLeft);

  const handleDismiss = async (row: DeadlineRow) => {
    setRows(prev => prev.filter(r => r.id !== row.id));
    try {
      await ServiceDashboard.dismissDeadline(row.id);
    } catch {
      // Silently fail; row already removed optimistically.
    }
  };

  const getTypeLabel = (row: DeadlineRow): string => {
    if (row.type === 'anbudsinlamning' && row.daysLeft < 0) {
      return t('deadlines.type.utvärderaAnbud', 'Utvärdera anbud');
    }
    // Fallback guards against deploy skew (backend emits a type the frontend
    // bundle doesn't know yet) so an unknown type can't blank the whole table.
    return t(...(TYPE_LABELS[row.type] ?? ['', row.type]));
  };

  const columns: Column<DeadlineRow>[] = [
    {
      title: t('deadlinesMessages.descriptionColumn', 'Beskrivning'),
      field: 'description',
    },
    {
      title: t('deadlinesMessages.typeColumn', 'Typ'),
      field: 'type',
      render: (row) => <TypeBadge label={getTypeLabel(row)} />,
    },
    {
      title: t('deadlinesMessages.deadlineColumn', 'Deadline'),
      field: 'deadline',
      render: (row) => moment(row.deadline).format('D MMMM YYYY'),
    },
    {
      title: t('deadlinesMessages.daysLeftColumn', 'Dagar kvar'),
      field: 'daysLeft',
      customSort: (a, b) => a.daysLeft - b.daysLeft,
      render: (row) => <DaysLeftBadge daysLeft={row.daysLeft} />,
    },
    {
      title: '',
      field: 'id',
      sorting: false,
      width: '50px',
      cellStyle: { padding: '0 8px' },
      render: (row) => (
        <button
          className={`${styles.dismissBtn} p-1 rounded hover:bg-gray-200`}
          title={t('deadlines.dismiss', 'Markera som klar')}
          onClick={(e) => {
            e.stopPropagation();
            handleDismiss(row);
          }}
        >
          <CheckIcon style={{ fontSize: 16, color: 'var(--gray-600)' }} />
        </button>
      ),
    },
  ];

  return (
    <>
      <div className={styles.metricsGrid}>
        <Card className={styles.metricCard}>
          <div className={styles.metricCardText}>
            <p className={styles.metricCardTitle}>{t('deadlinesMessages.metrics.overdue', 'Försenade')}</p>
            <p className={styles.metricCardNumber} style={{ color: BADGE_COLORS.red.text }}>{overdueCount}</p>
            <p className={styles.metricCardSubtitle}>{t('deadlinesMessages.metrics.overdueSubtitle', 'Passerat deadline')}</p>
          </div>
          <div className={styles.metricCardIcon} style={{ backgroundColor: BADGE_COLORS.red.bg }}>
            <WarningAmberIcon style={{ fontSize: 16, color: BADGE_COLORS.red.text }} />
          </div>
        </Card>

        <Card className={styles.metricCard}>
          <div className={styles.metricCardText}>
            <p className={styles.metricCardTitle}>{t('deadlinesMessages.metrics.soon', 'Förfaller snart')}</p>
            <p className={styles.metricCardNumber} style={{ color: BADGE_COLORS.yellow.text }}>{soonCount}</p>
            <p className={styles.metricCardSubtitle}>{t('deadlinesMessages.metrics.soonSubtitle', 'Inom 7 dagar')}</p>
          </div>
          <div className={styles.metricCardIcon} style={{ backgroundColor: BADGE_COLORS.yellow.bg }}>
            <ScheduleIcon style={{ fontSize: 16, color: BADGE_COLORS.yellow.text }} />
          </div>
        </Card>

        <Card className={styles.metricCard}>
          <div className={styles.metricCardText}>
            <p className={styles.metricCardTitle}>{t('deadlinesMessages.metrics.upcoming', 'Kommande')}</p>
            <p className={styles.metricCardNumber} style={{ color: BADGE_COLORS.gray.text }}>{upcomingCount}</p>
            <p className={styles.metricCardSubtitle}>{t('deadlinesMessages.metrics.upcomingSubtitle', 'Mer än 7 dagar')}</p>
          </div>
          <div className={styles.metricCardIcon} style={{ backgroundColor: BADGE_COLORS.gray.bg }}>
            <CalendarMonthIcon style={{ fontSize: 16, color: BADGE_COLORS.gray.text }} />
          </div>
        </Card>
      </div>

      <div className={styles.typeFilters}>
        <button
          className={`${styles.typePill} ${typeFilters.size === 0 ? styles.typePillActive : ''}`}
          onClick={() => setTypeFilters(new Set())}
        >
          {t('deadlinesMessages.typeFilterAll', 'Alla')}
        </button>
        {typeOptions.map(opt => (
          <button
            key={opt.value}
            className={`${styles.typePill} ${typeFilters.has(opt.value) ? styles.typePillActive : ''}`}
            onClick={() => toggleTypeFilter(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className={styles.deadlinesTable}>
        <DataTable
          isLoading={isLoading}
          columns={columns}
          data={visibleRows}
          pageSize={15}
          sorting
          thirdSortClick={false}
          defaultOrderBy={3}
          defaultOrderDirection="asc"
          onRowClick={(_, row) => {
            if (row) history.push(`${orgUrl}${row.href}`);
          }}
        />
      </div>
    </>
  );
};

export default DeadlinesTab;
